import type { JSX } from 'react';
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';

export function RequirePlatformAccess(): JSX.Element {
  const auth = useAuth();
  const location = useLocation();
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  if (auth.isLoading) {
    return (
      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading platform workspace...</Typography>
      </Stack>
    );
  }

  if (!auth.bootstrap) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!auth.canAccessPlatform) {
    return <Navigate to="/" replace state={{ workspaceUnavailable: 'platform' }} />;
  }

  if (auth.bootstrap.mode === 'company') {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
        <Card variant="outlined" sx={{ maxWidth: 760, width: '100%' }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h5">Switch to platform workspace</Typography>
              <Typography color="text.secondary">
                Platform administration runs in identity mode. Switching will end the current
                company-scoped session and open the platform workspace.
              </Typography>
              {switchError && <Alert severity="error">{switchError}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  disabled={isSwitching}
                  onClick={() => {
                    if (!auth.selectIdentity) {
                      return;
                    }

                    setSwitchError(null);
                    setIsSwitching(true);
                    void auth.selectIdentity()
                      .catch((error: unknown) => {
                        setSwitchError(
                          error instanceof Error ? error.message : 'Unable to switch to platform workspace',
                        );
                      })
                      .finally(() => {
                        setIsSwitching(false);
                      });
                  }}
                >
                  {isSwitching ? 'Switching...' : 'Switch to platform workspace'}
                </Button>
                <Button component={NavLink} to="/" variant="outlined">
                  Stay in company workspace
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return <Outlet />;
}
